import React from 'react';
import { T, NUMERIC } from '../customer/customers.theme';
import { EMPHASIS, grouped } from './dashboard.theme';

/**
 * The pieces the dashboard is made of.
 *
 * Everything here is presentation only — no fetching, no arithmetic. The
 * numbers arrive already worked out by `dashboard.selectors.js`, which is what
 * makes both halves testable.
 */

/* ── icons ──────────────────────────────────────────────────────────────── */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export const ArrowIcon = ({ size = 11, down }) => (
  <svg {...base(size)} strokeWidth={3}>
    <path d={down ? 'M12 5v13M6 12l6 6 6-6' : 'M12 19V6M6 12l6-6 6 6'} />
  </svg>
);

export const ClockIcon = ({ size = 16, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5l3 2" />
  </svg>
);

export const TagIcon = ({ size = 16, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M19 5L5 19" />
    <circle cx="7.5" cy="7.5" r="2.5" />
    <circle cx="16.5" cy="16.5" r="2.5" />
  </svg>
);

export const VoidIcon = ({ size = 16, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </svg>
);

export const MenuIcon = ({ size = 16, width = 2 }) => (
  <svg {...base(size)} strokeWidth={width}><path d="M4 6.5h16M4 12h16M4 17.5h9" /></svg>
);

export const BagIcon = ({ size = 22, width = 1.7 }) => (
  <svg {...base(size)} strokeWidth={width}>
    <path d="M4 7h16l-1.5 12H5.5z" />
    <path d="M9 7V5.5a3 3 0 0 1 6 0V7" />
  </svg>
);

/* ── containers ─────────────────────────────────────────────────────────── */

export function Panel({ title, subtitle, action, children, style }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '18px 20px 16px',
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        boxSizing: 'border-box',
        minWidth: 0,
        ...style,
      }}
    >
      {title ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            paddingBottom: 14,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{title}</span>
            {subtitle ? (
              <span style={{ fontSize: 11.5, color: T.inkFaint }}>{subtitle}</span>
            ) : null}
          </div>
          {action || null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/* ── figures ────────────────────────────────────────────────────────────── */

/**
 * The change against the comparison period.
 *
 * Green for up and red for down, with an arrow beside it — the arrow is what
 * carries the direction for anyone who cannot separate the two colours.
 */
export function DeltaChip({ delta, suffix }) {
  if (!delta || delta.percent === null || delta.direction === 'flat') return null;
  const down = delta.direction === 'down';

  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 9px',
        background: down ? T.dangerTint : T.paidTint,
        borderRadius: T.radiusPill,
        fontSize: 11.5,
        fontWeight: 700,
        color: down ? T.danger : T.paid,
        whiteSpace: 'nowrap',
      }}
    >
      <ArrowIcon down={down} />
      {Math.abs(delta.percent)}%{suffix ? ` ${suffix}` : ''}
    </span>
  );
}

/** The one number the screen leads with. */
export function HeroTile({ label, value, delta, footnote, children }) {
  return (
    <Panel style={{ justifyContent: 'space-between', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.inkSoft }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 44,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: -1.2,
              color: T.ink,
            }}
          >
            {value}
          </span>
          <span style={{ marginBottom: 6 }}>{delta}</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>{footnote}</span>
        {children || null}
      </div>
    </Panel>
  );
}

export function StatTile({ label, value, accent, note }) {
  return (
    <Panel style={{ justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: T.inkSoft }}>{label}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 27, fontWeight: 700, letterSpacing: -0.6, color: T.ink }}>
            {value}
          </span>
          {accent || null}
        </div>
        <span style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>{note}</span>
      </div>
    </Panel>
  );
}

/* ── marks ──────────────────────────────────────────────────────────────── */

/**
 * A part-to-whole bar: segments separated by the surface rather than outlined.
 *
 * Segments below 2% of the whole are drawn at 2% so they stay visible; the
 * figures beside the bar are the truth, and this only keeps a small slice from
 * disappearing entirely.
 */
export function SplitBar({ parts, colors, height = 18 }) {
  const total = parts.reduce((sum, part) => sum + Math.max(0, Number(part.value || 0)), 0);

  if (!total) {
    return <div style={{ height, background: EMPHASIS.track, borderRadius: 4 }} />;
  }

  return (
    <div style={{ display: 'flex', gap: 2, height, borderRadius: 4, overflow: 'hidden' }}>
      {parts
        .filter((part) => Number(part.value || 0) > 0)
        .map((part, index) => (
          <div
            key={part.key || index}
            title={part.label}
            style={{
              flexGrow: Math.max((Number(part.value) / total) * 100, 2),
              flexBasis: 0,
              background: colors[index % colors.length],
              borderRadius: 4,
            }}
          />
        ))}
    </div>
  );
}

/** A legend row: swatch, name, count, figure. Identity never rests on colour. */
export function LegendRow({ color, label, count, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <span style={{ width: 9, height: 9, background: color, borderRadius: 3, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: T.ink }}>{label}</span>
      {count !== undefined ? (
        <span style={{ fontSize: 12.5, color: T.inkFaint, ...NUMERIC }}>{grouped(count)}</span>
      ) : null}
      <span
        style={{
          minWidth: 74,
          textAlign: 'right',
          fontSize: 12.5,
          fontWeight: 700,
          color: T.ink,
          ...NUMERIC,
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** A labelled bar in a list — one hue for every row, because rank is not identity. */
export function BarRow({ label, value, share, muted, labelWidth = 78 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        title={label}
        style={{
          width: labelWidth,
          flexShrink: 0,
          fontSize: 12,
          color: muted ? T.inkFaint : T.inkSoft,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0, height: 14, background: EMPHASIS.track, borderRadius: 4 }}>
        <div
          style={{
            width: `${Math.max(0, Math.min(100, share))}%`,
            height: 14,
            background: muted ? T.inkGhost : EMPHASIS.accent,
            borderRadius: 4,
          }}
        />
      </div>
      <span
        style={{
          minWidth: 62,
          textAlign: 'right',
          fontSize: 12,
          fontWeight: 700,
          color: muted ? T.inkFaint : T.ink,
          ...NUMERIC,
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ── states ─────────────────────────────────────────────────────────────── */

export function DashboardKeyframes() {
  return (
    <style>
      {'@keyframes dalabtireDashPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }'}
    </style>
  );
}

/** Grey blocks on the first load only. */
export function Skeleton({ height = 14, width = '100%', radius = 4 }) {
  return (
    <div
      style={{
        height,
        width,
        background: T.hairline,
        borderRadius: radius,
        animation: 'dalabtireDashPulse 1.4s ease-in-out infinite',
      }}
    />
  );
}

export function EmptyPanel({ title, note }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '38px 16px',
        textAlign: 'center',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 46,
          height: 46,
          border: `1.5px dashed ${T.borderStrong}`,
          borderRadius: T.radiusPill,
          color: T.inkGhost,
        }}
      >
        <BagIcon />
      </span>
      <span style={{ fontSize: 13.5, color: T.inkSoft }}>{title}</span>
      {note ? (
        <span style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>{note}</span>
      ) : null}
    </div>
  );
}
